import {observer} from 'mobx-react-lite'
import React, {useEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {CompanyPreferencesClient} from '@client/company-preferences-client'
import {Sync} from '@client/sync'
import {GoogleCalendarSettingsPopup} from '@components/account/profile/google-calendar-settings-popup/google-calendar-settings-popup'
import {Input} from '@components/common/base/input'
import {showDeleteConfirm} from '@components/common/base/notifications'
import {Button, Link} from '@components/common/button/button'
import {SmallHelp} from '@components/common/help/help'
import {IconTabler} from '@components/common/icon-tabler/icon-tabler'
import {Loading} from '@components/common/loading/loading'
import {Popup} from '@components/common/popup/popup'
import {UserAvatar} from '@components/user-avatar'
import {
  useClickAway,
  useForceUpdate,
  useGlobalKeyDown,
  useSyncEvent
} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {ApiUi, UiSlot} from '../../../api/components'
import {EmailChangePopup} from './email-change-popup'
import EmailIcon from './icon-email.svg'
import NotificationsIcon from './icon-notifications.svg'
import PassIcon from './icon-pass.svg'
import {ProfileContext, ProfileStore, useProfileStore} from './profile-store'

export const Profile = () => {
  const [store] = useState(() => new ProfileStore())

  return (
    <ProfileContext.Provider value={store}>
      <ProfileBase />
    </ProfileContext.Provider>
  )
}

const alert = <i className="mr-4 fi-alert" />

// eslint-disable-next-line max-lines-per-function
const ProfileBase = observer(() => {
  const ref = useRef<HTMLInputElement | null>(null)
  const [state, store] = useProfileStore()

  useEffect(() => {
    store.update()
    store.syncGoogleCalendarSettingsData()
  }, [])

  useEffect(() => {
    if (state.googleCalendarSettingsStatus === 'isReady') {
      store.openGoogleSettingsPopup()
    }
    if (state.googleCalendarSettingsStatus === 'off') {
      store.closeGoogleSettingsPopup()
    }
  }, [state.googleCalendarSettingsStatus])

  const forceUpdate = useForceUpdate()
  useSyncEvent(
    'profile',
    {
      debounceTime: 50,
      filter: globals.currentUserId
    },
    () => {
      store.update()
      forceUpdate()
    }
  )

  useSyncEvent(
    'current',
    {
      debounceTime: 150,
      filter: globals.currentUserId
    },
    () => {
      store.update()
    }
  )

  const saveSettings = () => {
    if (state.oldRealName !== state.realName) {
      store.saveSettings()
    }
  }

  const onClickOutside = () => {
    saveSettings()
  }

  useClickAway(ref, onClickOutside)

  const box = Sync.getCurrentSandbox()
  const canEditOwnEmail = CompanyPreferencesClient.isAllowedForCurrentUser(
    box.companyId,
    'editOwnEmail'
  )

  return (
    <>
      <div className="flex flex-col">
        <div className="flex items-center mb-16">
          <div onClick={store.uploadPhoto}>
            <UserAvatar user={Sync.getCurrentUser()} opacity={1} size={80} />
          </div>

          <div className="flex flex-col gap-8 ml-16">
            <Link onClick={store.uploadPhoto}>
              {Lang.msg.profilePage.uploadPhoto}
            </Link>

            <Link
              onClick={async () => {
                if (
                  await showDeleteConfirm(
                    Lang.msg.profilePage.deletePhotoConfirm
                  )
                ) {
                  store.deletePhoto()
                }
              }}
              className="text-third-old"
            >
              {Lang.msg.profilePage.deletePhoto}
            </Link>
          </div>
        </div>

        <div>
          <div className="flex gap-8 mb-24">
            <div ref={ref} className="max-w-240">
              <Input
                autoFocus={false}
                placeholder={Lang.msg.displayedName}
                size="40"
                value={state.realName}
                onChange={store.changeRealName}
                onSubmit={saveSettings}
                onCancel={store.revertSettings}
              />
              {state.error && (
                <div className="text-error-old">
                  {alert}
                  <span>{state.error}</span>
                </div>
              )}
            </div>
            {state.oldRealName !== state.realName && (
              <div ref={ref} className="flex gap-8">
                <Link
                  onClick={() => store.revertSettings()}
                  className="text-third-old"
                >
                  {Lang.msg.cancel}
                </Link>
                <Link onClick={saveSettings}>{Lang.msg.save}</Link>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-14">
            <div className="flex items-center">
              <div className="flex mr-4 text-third-old">
                <PassIcon />
              </div>
              <Link onClick={() => store.changePassShow(true)}>
                {Lang.msg.changePass}
              </Link>
            </div>
            {canEditOwnEmail && (
              <div className="flex items-center">
                <div className="flex mr-4 text-third-old">
                  <EmailIcon />
                </div>
                <Link onClick={store.openChangeEmailPopup}>
                  {Lang.msg.profilePage.changeEmail}
                </Link>
              </div>
            )}
            <div className="flex items-center">
              <div className="flex mr-4 text-third-old">
                <NotificationsIcon />
              </div>
              {state.mailLinkWait ? (
                <Loading size="sm" />
              ) : (
                <Link>
                  <a href={state.mailLink} target="_blank">
                    {Lang.msg.profilePage.emailNotificationSettings}
                  </a>
                </Link>
              )}
            </div>
            {!globals.standalone && !Util.isDesktop() && (
              <div className="flex gap-4">
                <div className="flex text-third-old">
                  <div className="w-24 h-24 flex">
                    <IconTabler
                      className="m-auto"
                      icon="IconCustomBoardCalendar"
                    />
                  </div>
                </div>
                {state.waitGoogleCalendar && <Loading size="sm" />}
                {!state.waitGoogleCalendar && (
                  <>
                    {state.googleCalendarSettingsStatus === 'error' && (
                      <div className="text-warning pt-2">
                        {Lang.msg.googleCalendar.errorRetrievingData}{' '}
                        <Link onClick={store.startGCIntegration}>
                          {Lang.msg.googleCalendar.setupAgain}
                        </Link>
                      </div>
                    )}
                    {state.googleCalendarSettingsStatus === 'active' && (
                      <div className="text-primary pt-2">
                        {Lang.msg.googleCalendar.integrationWithGoogleCalendar}{' '}
                        <span className="font-semibold">
                          {Lang.msg.googleCalendar.isEnabled}{' '}
                        </span>
                        <Link onClick={store.openGoogleSettingsPopup}>
                          {Lang.msg.common.setup}
                        </Link>
                      </div>
                    )}
                    {(!state.googleCalendarSettingsStatus ||
                      !['active', 'error'].includes(
                        state.googleCalendarSettingsStatus
                      )) && (
                      <>
                        {!state.waitGoogleCalendar && (
                          <Link onClick={store.startGCIntegration}>
                            {
                              Lang.msg.googleCalendar
                                .integrationWithGoogleCalendar
                            }
                          </Link>
                        )}
                        <div className="inline-flex items-center">
                          <SmallHelp name="googleCalendar" />
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            <UiSlot name="profile-page-profile-bottom" />
            <ApiUi
              category="profile-page-profile-bottom"
              id={'not-defined' as DataObjectId}
            />
          </div>
        </div>
      </div>
      {state.changePassShow && <PasswordChange />}
      {state.changeEmailPopupShow && <EmailChangePopup />}
      {state.googleIntegrationPopupShow && <GoogleCalendarSettingsPopup />}
    </>
  )
})

const TwPassLabel = tw.div`
  mb-4
  leading-main
  text-12
  text-secondary-old
`

export const TwCenter = tw.div`
  flex
  justify-center
  text-third-old
  mt-8
`

const TwInput = tw.input`
  flex
  items-center
  h-32
  pl-8
  pr-8
  border
  border-solid
  outline-none
  border-main-old
  text-main-old
  focus:bg-input-old
  focus-within:border-action-default
  rounded-4
  bg-input-old
  text-14
  mb-0
  w-256
`

// eslint-disable-next-line max-lines-per-function
const PasswordChange = observer(() => {
  const [state, store] = useProfileStore()

  const close = () => {
    store.changePassShow(false)
    return true
  }

  useGlobalKeyDown({escape: close})

  const submit = () => {
    void store.submitChangePass()
  }

  const footer = state.changePassWait ? (
    <TwCenter>
      <Loading />
    </TwCenter>
  ) : (
    <Button onClick={submit}>{Lang.msg.changePass}</Button>
  )

  return (
    <Popup
      width={442}
      autoHeight={true}
      close={close}
      title={Lang.msg.profilePage.passwordChange}
      footer={footer}
    >
      <div className="text-14 mb-34">
        <div className="mb-12 leading-main">
          {Lang.msg.profilePage.passwordChangeAware}
        </div>
        <ul className="leading-normal">
          <li>
            {Lang.msg.profilePage.passwordChangeSentence1}{' '}
            <span className="leading-normal text-error-old text-12">
              {Lang.msg.profilePage.passwordChangeSentence1_1}
            </span>
          </li>
          <li>{Lang.msg.profilePage.passwordChangeSentence2}</li>
          <li>
            {Lang.msg.profilePage.passwordChangeSentence3}{' '}
            <a
              href={
                'https://en.wikipedia.org/wiki/List_of_the_most_common_passwords'
              }
              target="_blank"
              className="pb-1 border-0 border-solid text-main-old border-b-1"
            >
              {Lang.msg.profilePage.passwordChangeSentence4}
            </a>
          </li>
        </ul>
      </div>

      <div className="flex flex-col gap-12 mx-64">
        <div>
          <TwPassLabel>{Lang.msg.profilePage.currentPassword}</TwPassLabel>
          <TwInput
            type="password"
            value={state.changePassOldPassword}
            onChange={e => store.changePassOld(e.target.value)}
          />
        </div>
        <div>
          <TwPassLabel>{Lang.msg.profilePage.newPassword}</TwPassLabel>
          <TwInput
            type="password"
            value={state.changePassNewPassword}
            onChange={e => store.changePassNew(e.target.value)}
          />
        </div>
        <div>
          <TwPassLabel>{Lang.msg.profilePage.newPasswordAgain}</TwPassLabel>
          <TwInput
            type="password"
            value={state.changePassNewAgainPassword}
            onChange={e => store.changePassNewAgain(e.target.value)}
          />
        </div>
        {state.changePassError && (
          <div className="text-error-old">
            {alert}
            <span>{state.changePassError}</span>
          </div>
        )}
      </div>
    </Popup>
  )
})
